/* eslint-disable react-refresh/only-export-components */
/**
 * <ScribeFormBridgeProvider>
 *
 * Phase 3 item 3.11 (LD-1 / LD-5) — rewired for the new structured AI shape.
 *
 * The scribe controls and the Progress Note form are siblings under
 * `<EncounterWorkspace>`. The Stop flow needs to be able to:
 *
 *   - push the structured AI draft (summary + conditions + procedures +
 *     medications + observations + allergies + serviceRequests + billing)
 *     into the form's RHF state,
 *   - and ask the page chrome to swap the visible pane from "Zoom call full
 *     width" to "Progress Note visible".
 *
 * The `addScreenings` callback is gone (LD-5 killed AI-suggested screenings).
 * The `setAnnotatedMedications` callback is gone (the safety-check endpoint
 * was deleted on the BE; allergy badges now flow through the AI draft itself).
 */
import { createContext, use, useCallback, useMemo, useRef, type ReactNode } from 'react'
import type {
  AiAllergyDraft,
  AiConditionDraft,
  AiMedicationDraft,
  AiObservationDraft,
  AiProcedureDraft,
  AiServiceRequestDraft,
  BillingSuggestionDraft,
  VisitSummaryDraft,
} from '@/pages/Call/hooks/useFinalizeScribe'

/**
 * The structured AI draft payload pushed into the form. Matches the
 * fields the form's RHF state expects under
 * `ProgressNoteEditFormValues.{summary, conditions, procedures, …}`.
 */
export interface StructuredScribeDraft {
  /**
   * Visit narrative (chief complaint / HPI / ROS / Assessment / Plan). Optional
   * because the AI may return a partial payload — e.g. structured arrays
   * without a summary. The form merge preserves the existing carry-forward
   * summary when this is `undefined`.
   */
  summary?: VisitSummaryDraft
  conditions: AiConditionDraft[]
  procedures: AiProcedureDraft[]
  medications: AiMedicationDraft[]
  observations: AiObservationDraft[]
  allergies: AiAllergyDraft[]
  serviceRequests: AiServiceRequestDraft[]
  /**
   * AI-suggested E/M billing code + add-ons. Optional for the same reason as
   * `summary` — partial AI payloads (no billing) should still flow through
   * `applyAiDraftToFormValues` instead of being dropped entirely.
   */
  billing?: BillingSuggestionDraft
}

/**
 * Options passed to `populateFromAi` to control what the form does AFTER it
 * applies the AI draft.
 *
 *  - `autoSubmit` (FE-4): when `true`, the form fires its existing Save code
 *    path immediately after the AI draft commits to RHF state. This is what
 *    the Zoom panel's "Create Progress Note" flow uses to land the doctor on
 *    the Progress Note viewer directly, without an intermediate edit-form
 *    pit-stop where they'd have to click Save themselves.
 *  - On submit failure the form keeps the edit pane mounted (the existing red
 *    toast in `ProgressNoteEdit.handleSubmit` fires), so the doctor still has
 *    a path forward.
 */
export interface PopulateFromAiOptions {
  autoSubmit?: boolean
}

/**
 * Callback the Progress Note form registers so the scribe orchestrator can
 * push the AI structured draft into all relevant fields at once. The form
 * implementation should call `form.setValue('summary', ...)`,
 * `replace(conditionsField, ...)`, etc. — preserving any existing doctor
 * edits to fields the AI didn't touch.
 *
 * Returns a Promise that resolves once the form is done processing the draft
 * (and, when `opts.autoSubmit` is set, once the auto-submit POST has settled
 * — successfully or not). Callers (the Zoom panel's End-Session flow) await
 * this so they can keep the spinner overlay up until navigation fires, so the
 * doctor never sees a flash of the edit form between "Create Progress Note"
 * and the read-only viewer.
 *
 * Implementations that don't need to signal completion may return `void` —
 * the bridge wraps such returns as `Promise.resolve()`.
 */
export type PopulateFromAi = (
  draft: StructuredScribeDraft,
  opts?: PopulateFromAiOptions,
) => Promise<void> | void

/**
 * Callback the page chrome (CallPage) registers so the scribe flow can
 * surface the Progress Note pane after a successful Create-Progress-Note
 * finalize. Optional — side-panel mode has no chrome to swap, so the
 * registered callback there is a no-op.
 */
export type ShowSoapNote = () => void

/**
 * Callback the Progress Note form registers so the End-Session flow can
 * always land the doctor on a saved preliminary Progress Note, even when the
 * AI scribe never produced a draft (scribe disabled, session lost,
 * generate-draft 4xx/5xx, …). The implementation simply fires the form's
 * existing Save code path against whatever values are currently in RHF
 * state (carry-forward seeds + any manual edits the doctor made). On success
 * the form navigates to the new DocumentReference's viewer; on failure the
 * form's red toast surfaces and the edit form stays mounted for retry.
 */
export type SubmitCurrent = () => Promise<void> | void

/**
 * Callback the Progress Note form registers so the End-Session flow can PERSIST
 * the current draft (its debounced autosave, flushed immediately) WITHOUT
 * revealing/navigating — makes an AI draft durable before the disclaimer modal
 * so closing the tab at the modal can't lose it (PD-1181).
 */
export type FlushDraft = () => Promise<void> | void

export interface PendingAiDraft {
  draft: StructuredScribeDraft
  opts: PopulateFromAiOptions
  /**
   * Signal that the form has finished handling the cached draft. Resolves
   * the awaiting `populateFromAi(...)` promise the caller (Zoom panel) is
   * holding. Call once — calling again is a no-op.
   *
   * For `opts.autoSubmit === true`, the form should `resolve()` AFTER its
   * auto-submit POST settles (success path: navigation has already fired;
   * failure path: the red toast has surfaced and the doctor will see the
   * edit form).
   *
   * For the default (no autoSubmit) path the form resolves as soon as the
   * draft is applied to RHF state.
   */
  resolve: () => void
  /**
   * Signal that something went catastrophically wrong while applying the
   * cached draft. Surfaces as a rejection on the awaiting `populateFromAi`
   * promise. Use sparingly — most failure modes (POST error, validation
   * error) already surface their own toast and should resolve, not reject.
   */
  reject: (err: unknown) => void
}

export interface ScribeFormBridgeContextValue {
  registerPopulateFromAi: (fn: PopulateFromAi | null) => void
  registerShowSoapNote: (fn: ShowSoapNote | null) => void
  registerSubmitCurrent: (fn: SubmitCurrent | null) => void
  registerFlushDraft: (fn: FlushDraft | null) => void
  populateFromAi: (draft: StructuredScribeDraft, opts?: PopulateFromAiOptions) => Promise<void>
  showSoapNote: ShowSoapNote
  /**
   * Trigger the registered Progress Note form's Save handler against its
   * current RHF state. Used by the End-Session flow when the AI scribe did
   * not produce a draft (so `populateFromAi` is skipped) but the doctor still
   * expects an unlocked preliminary Progress Note to exist after ending the
   * call. Resolves once the submit POST settles + navigation fires. If no
   * form is registered yet, resolves immediately (the form's own hydrate path
   * will save on next user click).
   */
  submitCurrentDraft: () => Promise<void>
  /**
   * Persist the registered form's current RHF draft immediately (flush the
   * debounced autosave) without navigating/revealing. Resolves once the POST
   * settles; resolves immediately when no form is registered. PD-1181.
   */
  flushDraft: () => Promise<void>
  /**
   * Peek at whether a draft is cached and waiting to be flushed into a form
   * that has not yet registered a `PopulateFromAi` callback. The form's
   * hydrate effect uses this to skip its empty `methods.reset(...)` when an
   * AI draft is about to be applied — otherwise the empty reset clobbers the
   * AI values (the race the populate microtask was meant to win, but
   * doesn't reliably under React 19's concurrent scheduling).
   */
  hasPendingAiDraft: () => boolean
  /**
   * Synchronously consume and clear the cached AI draft. Returns the draft
   * + the opts the caller asked for (e.g. `autoSubmit`) or `null`. Callers
   * (the form's hydrate effect) use this to read the draft and apply it
   * atomically via `methods.reset(merged)`, eliminating the empty-reset /
   * populate-microtask race.
   */
  consumeAiDraft: () => PendingAiDraft | null
}

const ScribeFormBridgeContext = createContext<ScribeFormBridgeContextValue | null>(null)

/**
 * Returns `null` outside a provider so callers can null-check gracefully.
 */
export function useScribeFormBridge(): ScribeFormBridgeContextValue | null {
  return use(ScribeFormBridgeContext)
}

export function ScribeFormBridgeProvider({ children }: { children: ReactNode }) {
  const populateRef = useRef<PopulateFromAi | null>(null)
  const showSoapNoteRef = useRef<ShowSoapNote | null>(null)
  const submitCurrentRef = useRef<SubmitCurrent | null>(null)
  const flushDraftRef = useRef<FlushDraft | null>(null)
  /**
   * Pending AI draft + opts. Set when `populateFromAi(draft, opts)` is called
   * before any form registers a `PopulateFromAi` callback (i.e. when the AI
   * finalize step fires while the user is still browsing a past Progress
   * Note and the edit form is not yet mounted — see CallPage.tsx's
   * `isBrowsingPastNote` branch).
   *
   * When a form later registers via `registerPopulateFromAi(fn)`, it
   * immediately receives this cached draft and the slot is cleared so a
   * subsequent re-mount does not re-populate (the doctor may have edited
   * fields in the meantime).
   *
   * `resolve` / `reject` propagate the awaiting caller's promise from
   * `populateFromAi(...)` (now Promise-returning) so the Zoom panel can keep
   * the End-Session spinner up until the form has finished applying the AI
   * draft and — when `opts.autoSubmit` is set — until the auto-submit POST
   * has settled. The form invokes `resolve()` once it's done.
   */
  const pendingDraftRef = useRef<{
    draft: StructuredScribeDraft
    opts: PopulateFromAiOptions
    resolve: () => void
    reject: (err: unknown) => void
  } | null>(null)

  const registerPopulateFromAi = useCallback((fn: PopulateFromAi | null) => {
    populateRef.current = fn
    // Flush any pending draft as soon as a real form callback is registered.
    // This unblocks the Bug-2 path: ZoomPanel's "Create Progress Note" pushes
    // the AI draft through the bridge BEFORE CallPage navigates away from a
    // past-note URL; by the time `<ProgressNoteEdit>` actually mounts and
    // registers, the original `populateFromAi` call has already fired into a
    // null ref. Cache + flush bridges that gap deterministically.
    //
    // Flush is deferred via `queueMicrotask` so it runs AFTER all of the
    // newly-mounted form's sibling `useEffect`s — most importantly the
    // hydration effect in `<ProgressNoteEdit>` that calls `methods.reset(empty)`
    // when there is no `provenance`/`documentReference` to hydrate. Without
    // the microtask gap, the bridge would populate fields and then the
    // hydration reset would immediately wipe them.
    if (fn && pendingDraftRef.current) {
      const pending = pendingDraftRef.current
      pendingDraftRef.current = null
      queueMicrotask(() => {
        // `populateRef.current` may have been swapped out (or nulled) between
        // scheduling and running the microtask — check the latest ref rather
        // than the captured `fn`, mirroring how `populateFromAi` reads.
        const cb = populateRef.current
        if (!cb) {
          // Form unregistered before we got a chance to flush — resolve the
          // awaiting promise so the caller's spinner doesn't dangle forever.
          pending.resolve()
          return
        }
        try {
          const ret = cb(pending.draft, pending.opts)
          if (ret && typeof ret.then === 'function') {
            ret.then(pending.resolve, pending.reject)
          } else {
            pending.resolve()
          }
        } catch (err) {
          pending.reject(err)
        }
      })
    }
  }, [])

  const registerShowSoapNote = useCallback((fn: ShowSoapNote | null) => {
    showSoapNoteRef.current = fn
  }, [])

  const registerSubmitCurrent = useCallback((fn: SubmitCurrent | null) => {
    submitCurrentRef.current = fn
  }, [])

  const submitCurrentDraft = useCallback(async (): Promise<void> => {
    const cb = submitCurrentRef.current
    if (!cb) return
    const ret = cb()
    if (ret && typeof ret.then === 'function') {
      await ret
    }
  }, [])

  const registerFlushDraft = useCallback((fn: FlushDraft | null) => {
    flushDraftRef.current = fn
  }, [])

  const flushDraft = useCallback(async (): Promise<void> => {
    const cb = flushDraftRef.current
    if (!cb) return
    const ret = cb()
    if (ret && typeof ret.then === 'function') {
      await ret
    }
  }, [])

  const populateFromAi = useCallback(
    (draft: StructuredScribeDraft, opts: PopulateFromAiOptions = {}): Promise<void> => {
      const cb = populateRef.current
      if (cb) {
        try {
          const ret = cb(draft, opts)
          if (ret && typeof ret.then === 'function') {
            return ret
          }
          return Promise.resolve()
        } catch (err) {
          return Promise.reject(err instanceof Error ? err : new Error(String(err)))
        }
      }
      // Form is not mounted yet (caller is mid-navigation away from a past-note
      // URL). Stash the draft so the next `registerPopulateFromAi(fn)` flushes
      // it. Last write wins — if the caller pushes a fresh draft before the
      // form registers, only the newest survives, and the prior call's promise
      // is resolved (the caller's intent has been superseded; resolving with
      // void avoids a dangling rejection toast).
      if (pendingDraftRef.current) {
        pendingDraftRef.current.resolve()
      }
      return new Promise<void>((resolve, reject) => {
        pendingDraftRef.current = { draft, opts, resolve, reject }
      })
    },
    [],
  )

  const showSoapNote = useCallback<ShowSoapNote>(() => {
    showSoapNoteRef.current?.()
  }, [])

  const hasPendingAiDraft = useCallback(() => pendingDraftRef.current !== null, [])

  const consumeAiDraft = useCallback((): PendingAiDraft | null => {
    const pending = pendingDraftRef.current
    pendingDraftRef.current = null
    if (!pending) return null
    // Hand the resolve/reject handles to the consumer (the form's hydrate
    // effect). The consumer settles the awaiting `populateFromAi` promise
    // once it's done — for `opts.autoSubmit === true` that's AFTER the POST
    // round-trips so the Zoom panel's spinner can stay up until navigation
    // fires. For the no-autoSubmit case, the consumer resolves as soon as
    // the draft has been applied to RHF state.
    return pending
  }, [])

  const value = useMemo<ScribeFormBridgeContextValue>(
    () => ({
      registerPopulateFromAi,
      registerShowSoapNote,
      registerSubmitCurrent,
      registerFlushDraft,
      populateFromAi,
      showSoapNote,
      submitCurrentDraft,
      flushDraft,
      hasPendingAiDraft,
      consumeAiDraft,
    }),
    [
      registerPopulateFromAi,
      registerShowSoapNote,
      registerSubmitCurrent,
      registerFlushDraft,
      populateFromAi,
      showSoapNote,
      submitCurrentDraft,
      flushDraft,
      hasPendingAiDraft,
      consumeAiDraft,
    ],
  )

  return (
    <ScribeFormBridgeContext.Provider value={value}>{children}</ScribeFormBridgeContext.Provider>
  )
}
